import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnChanges, SimpleChanges, OnDestroy, ChangeDetectorRef } from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import ArrayStore from 'devextreme/data/array_store';
import { DxDataGridComponent, DxDropDownBoxComponent } from 'devextreme-angular';
import { LookupService } from '../../../services/lookup.service';
import { FormGroup } from '@angular/forms';

@Component({
  selector: 'fp-dx-lookup',
  templateUrl: './fp-dx-lookup.component.html',
  styleUrls: ['./fp-dx-lookup.component.scss']
})
export class FpDxLookupComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('dropDownBox', { static: false }) dropDownBox: DxDropDownBoxComponent;
  @ViewChild('grid', { static: false }) grid: DxDataGridComponent;

  /** The source data array */
  @Input() dataSource: any[] = null;
  
  /** Entity Type for automatic data loading via LookupService */
  @Input() entityType: string;
  @Input() dataFilter: any[] = null; // Supports LinqPredicateDTO[]
  @Input() complexFilters: any[] = null;
  /** Alias for complexFilters as used in legacy code */
  @Input() complexDataFilter: any[] = null;

  @Input() customConfig: any = null;
  @Input() namespaceClass: string = null;

  // properties for compatibility (logic to be implemented if needed)
  @Input() isVirtualScroll: boolean = false;
  @Input() bindCompleteObject: boolean = false;
  @Input() appendToInput: string;
  @Input() isMultiple: boolean = false; 
  @Input() isRequired: boolean = false;

  @Input() formGroupInput: FormGroup;
  @Input() formControlNameInput: string;
  @Input() dropdownPosition: string;

  /** 
   * Property for backward compatibility with eqp-lookup:
   * Supports [selectedItem]="variable" or [(selectedItem)]="variable"
   */
  @Input() selectedItem: any;
  @Output() selectedItemChange = new EventEmitter<any>();
  @Output() onElementValueChanged = new EventEmitter<any>();

  @Output() valueSelected = new EventEmitter<any>();

  /** Two-way binding for selected value (key) */
  @Input() value: any;
  @Output() valueChange = new EventEmitter<any>();

  /** Expressions for value and display text */
  @Input() valueExpr: string = 'ID';
  @Input() displayExpr: string = 'Label';
  @Input() keyExpr: string = 'ID';

  /** Optional placeholder */
  @Input() placeholder: string = '';
  
  /** Disabled state */
  @Input() disabled: boolean = false;

  /** Show clear button */
  @Input() showClearButton: boolean = true;

  /** Grid height */
  @Input() gridHeight: number | string = 400;

  /** Paging configuration */
  @Input() pageSize: number = 10;
  @Input() showPaginator: boolean = true;

  /** Wrapper Class for styling */
  @Input() wrapperClass: string = 'header-lookup-ddb';

  /** Column Auto Width */
  @Input() columnAutoWidth: boolean = true;


  /** Columns configuration for the internal grid 
   * Example: [{ dataField: 'Label', caption: 'Name', minWidth: 300 }]
   */
  @Input() columns: any[] = [];

  /** Additional dropdown options */
  @Input() dropDownOptions: any = {
    width: 'auto',
    maxWidth: '90vw',
    wrapperAttr: { class: 'lookup-style-popup' },
  };

  /** Specific search logic function. If not provided, it builds a string from visible columns */
  @Input() searchExpr: any;

  @Input() allowAddNew: boolean = false;
  @Input() addItemLabel: string = '';
  @Output() onAddItem = new EventEmitter<string>();

  @Output() onSelectionChanged = new EventEmitter<any>();
  @Output() onValueChanged = new EventEmitter<any>();
  @Output() onOpened = new EventEmitter<any>();
  @Output() onClosed = new EventEmitter<any>();

  // Internal State
  gridDataSource: DataSource;
  dropDownBoxDataSource: DataSource;
  _internalData: any[] = [];
  
  isDropDownOpened: boolean = false;
  focusedRowIndex: number = 0;
  focusedRowKey: any = null;
  
  private searchTimer: any = null;
  private firstLoadCompleted: boolean = false;

  currentSearchText: string = '';
  isGridEmpty: boolean = false;

  constructor(private lookupService: LookupService, private cd: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dataSource'] || changes['entityType'] || changes['namespaceClass'] || changes['dataFilter'] || changes['complexDataFilter'] || changes['complexFilters']) {
      this.initDataSources();
    }
    
    // If value changes externally
    if (changes['value']) {
      this.focusedRowKey = changes['value'].currentValue;
      this.updateFormControl(this.focusedRowKey);
    }
    // If selectedItem changes externally and value is not set
    if (changes['selectedItem'] && changes['selectedItem'].currentValue) {
        if (!this.value) { // Only if value is not already providing a key
            const item = changes['selectedItem'].currentValue;
            // Use keyExpr or valueExpr to find ID
            const key = this.getValueFromExpr(item, this.valueExpr) || this.getValueFromExpr(item, this.keyExpr) || item['ID'];
            if (key) {
                this.value = key;
                this.focusedRowKey = key;
                this.updateFormControl(key);
            }
        }
    }

    // Force grid selection update when dataSource or value changes
    if (changes['value'] || changes['dataSource']) {
         setTimeout(() => {
             if (this.grid && this.grid.instance && this.value) {
                 const currentSelection = this.grid.instance.getSelectedRowKeys();
                 if (!currentSelection.includes(this.value)) {
                    this.grid.instance.selectRows([this.value], false);
                    this.focusedRowKey = this.value; // Sync focus
                 }
                 // If we had a search text pending that matches this value's display, verify?
                 // Not strictly needed if onGridSelectionChanged handles close
             }
         }, 200); // Increased timeout to ensure grid data is bound
    }
  }

  ngOnInit() {
    // Gestione colonne di default e regole di visualizzazione
    if (!this.columns || this.columns.length === 0) {
      this.columns = [{
        dataField: this.displayExpr,
        caption: this.placeholder || 'GENERIC_FIELDS.DESCRIPTION',
        minWidth: 250
      }];
    } else {
      // Elimina la colonna FullObject se non è specificata una proprietà all'interno (es. FullObject.Code)
      this.columns = this.columns.filter(c => c.dataField !== 'FullObject');
    if (this.showPaginator==false){
        this.pageSize = 0;
    }
      // Se presente la colonna label, imposta la caption uguale al placeholder
      if (this.placeholder) {
        const labelCol = this.columns.find(c => c.dataField === this.displayExpr);
        if (labelCol) {
          labelCol.caption = this.placeholder;
        }
      }
    }

    this.initDataSources();
          
    // Initialize from selectedItem if value is missing
    if (!this.value && this.selectedItem) {
        const item = this.selectedItem;
        const key = this.getValueFromExpr(item, this.valueExpr) || this.getValueFromExpr(item, this.keyExpr) || item['ID'];
        if (key) {
             this.value = key;
             this.focusedRowKey = key;
        }
    }
    
    // Initialize from form control if available
    if (this.formGroupInput && this.formControlNameInput) {
        const control = this.formGroupInput.get(this.formControlNameInput);
        if (control) {
            if (control.value != null && this.value == null) {
                // If form has value but component doesn't, sync component
                this.value = control.value;
                this.valueChange.emit(this.value);
                this.focusedRowKey = this.value;
            }
            
            // Subscribe to form value changes to keep sync from form -> component
            control.valueChanges.subscribe(val => {
                if (val !== this.value) {
                    this.value = val;
                    this.valueChange.emit(val);
                    this.focusedRowKey = val;
                }
            });
        }
    }
  }

  get isInvalid(): boolean {
      if (this.formGroupInput && this.formControlNameInput) {
          const control = this.formGroupInput.get(this.formControlNameInput);
          if (control) {
               return control.invalid && (control.touched || control.dirty);
          }
      }
      return false;
  }

  private updateFormControl(val: any) {
      if (this.formGroupInput && this.formControlNameInput) {
          const control = this.formGroupInput.get(this.formControlNameInput);
          if (control && control.value !== val) {
              control.setValue(val, { emitEvent: false }); // avoid loops
          }
      }
  }

  ngOnDestroy() {
    if (this.searchTimer) {
        clearTimeout(this.searchTimer);
    }
  }

  private initDataSources() {
    // Priority: 1. dataSource Input 2. Fetch from entityType
    if (this.dataSource && this.dataSource.length > 0) {
        this.setupData(this.dataSource);
    } else if (this.entityType) {
        // Merge complexDataFilter into complexFilters if present
        let filters = this.complexFilters;
        if (this.complexDataFilter) {
            filters = this.complexDataFilter;
        }

        let finalNamespace = this.namespaceClass;
        // Automatic namespace resolution for known types in Generics namespace
        const genericsEntities = ['Account', 'Nation', 'VatRate', 'GenericTable', 'Ateco', 'DocumentNote'];
        if (!finalNamespace && genericsEntities.includes(this.entityType)) {
            finalNamespace = 'FatturaXLS.Core.DAL.Models.Generics';
        }

        this.lookupService.GetLookupEntities(this.entityType, this.dataFilter, filters, this.customConfig, finalNamespace).then(
            (data) => {
                this.setupData(data || []);
            },
            (error) => {
                console.error(`Error loading lookup data for ${this.entityType}`, error);
                this.setupData([]);
            }
        );
    } else {
        this.setupData([]);
    }
  }

  private setupData(data: any[]) {
    this._internalData = data || [];
    const arrayStore = new ArrayStore({
      key: this.keyExpr,
      data: data
    });

    // Grid DataSource with Search
    this.gridDataSource = new DataSource({
      store: arrayStore,
      searchExpr: this.searchExpr || this.getDefaultSearchExpr()
    });

    // DropDownBox DataSource (unfiltered)
    this.dropDownBoxDataSource = new DataSource({
      store: arrayStore
    });
  }

  private getDefaultSearchExpr(): any[] {
     // If columns are provided, search on those fields
     // Otherwise search on displayExpr
     if (this.columns && this.columns.length > 0) {
         return this.columns.map(c => c.dataField);
     }
     return [this.displayExpr];
  }

  private isSearchIncomplete(dropDownBox: any): boolean {
    let widget = dropDownBox;
    // Prefer the object that has 'option' function (Widget)
    // If input has .instance which has .option, use that (Angular Component Wrapper)
    if (dropDownBox.instance && typeof dropDownBox.instance.option === 'function') {
        widget = dropDownBox.instance;
    }
    
    if (typeof widget.option !== 'function') {
        return false;
    }

    const displayValue = widget.option('displayValue') as any;
    const text = widget.option('text');
    const textValue = (text && text.length) ? text : undefined;
    const displayFirst = (displayValue && displayValue.length) ? displayValue[0] : undefined;
    return textValue !== displayFirst;
  }

  private clearSearchAndRefresh() {
    if(this.gridDataSource) {
        this.gridDataSource.searchValue(null);
    }

    if (this.grid && this.grid.instance) {
      this.grid.instance.clearFilter();
      this.grid.instance.clearFilter('search');
      this.grid.instance.option('searchPanel.text', '');
      this.grid.instance.refresh();
    }
  }

  /* Event Handlers */

  onDropDownBoxInput(e: any) {
    const ddb = e.component;
    const text = ddb.option('text');
    this.currentSearchText = text;
    // Force detection to ensure template sees the text immediately
    this.cd.detectChanges();

    if (this.searchTimer) clearTimeout(this.searchTimer);

    this.searchTimer = setTimeout(() => {
      
      // DevExtreme can emit onInput also during initial value/text hydration.
      // We check if it is a real user event if possible, though ddb input event might not nested 'event' property always.
      // But typical usage in document-details checked for e.event or e.originalEvent.
      // Here e is the event object from (onInput).

      if (this.gridDataSource) {
        this.gridDataSource.searchValue(text || null);
      }

      if (this.isDropDownOpened && this.isSearchIncomplete(ddb as any)) {
         this.gridDataSource.load().then((items: any[]) => {
            this.isGridEmpty = !items || items.length === 0;
             if (items && items.length > 0) {
                 this.focusedRowKey = items[0][this.keyExpr];
                 this.focusedRowIndex = 0;
             }
             this.cd.detectChanges();
         }).catch(() => {}); // Handle exception
      } else {
          // Open only if it closed? 
          this.isDropDownOpened = true;
          this.cd.detectChanges();
      }

    }, 300);
  }

  onDropDownBoxOpened(e: any) {
    const ddb = e.component;
    
    // If opened via keyboard (ArrowDown), focus grid immediately
    if (ddb.isKeyDown) {
        ddb.isKeyDown = false;
        setTimeout(() => {
            if (this.grid && this.grid.instance) {
                try { this.grid.instance.focus(); } catch {}
            }
        }, 0);
        return;
    }

    // If search is incomplete (user typed but didn't select), try to focus first match
    if (this.firstLoadCompleted && this.isSearchIncomplete(ddb as any)) {
        this.gridDataSource.load().then((items: any[])=> {
            if (items && items.length > 0) {
                this.focusedRowKey = items[0][this.keyExpr];
            }
            // Force focus back to input
            setTimeout(() => { try { ddb.focus(); } catch {} }, 100);
        }).catch(() => {});
        return;
    }
    
    // Force focus back to input in general case
    setTimeout(() => { try { ddb.focus(); } catch {} }, 100);
    this.onOpened.emit(e);
  }

  onDropDownBoxClosed(e: any) {
      if (this.searchTimer) {
          clearTimeout(this.searchTimer);
          this.searchTimer = null;
      }
      
      const ddb = e.component;
      if (this.isSearchIncomplete(ddb as any)) {
          const displayValue = ddb.option('displayValue') as any;
          const displayFirst = (displayValue && displayValue.length) ? displayValue[0] : '';
          try { ddb.option('text', displayFirst); } catch {}
      }

      this.clearSearchAndRefresh();
      this.onClosed.emit(e);
  }

  private getValueFromExpr(item: any, expr: string): any {
      if (!item || !expr) return undefined;
      if (expr.includes('.')) {
          let val = item;
          const parts = expr.split('.');
          for (const part of parts) {
            val = val ? val[part] : undefined;
          }
          return val;
      }
      return item[expr];
  }

  onInternalValueChanged(e: any) {
    const newVal = e.value;

    // Prevent propagation of custom text values that do not match a valid item in the datasource.
    // This fixes the issue where pressing Enter on a search string clears the parent's selection.
    if (newVal != null && this._internalData && this._internalData.length > 0) {
        // Use == for loose equality (e.g. string "10" vs number 10)
        const match = this._internalData.find(item => {
            let val = this.getValueFromExpr(item, this.keyExpr);
            return val == newVal;
        });

        if (!match) {
            return;
        }
    }

    if (this.value !== newVal) {
        const prev = this.value;
        this.value = newVal;
        this.valueChange.emit(newVal);
        this.onValueChanged.emit({ value: newVal, previousValue: prev });
        this.updateFormControl(newVal);
        
        // Find item
        let item = null;
        if (this.gridDataSource) { 
             // We rely on grid selection change for full object mostly.
        }
    }
  }

  onDropDownBoxKeyDown(e: any) {
      const keyCode = e.event.keyCode;
      const ddb = e.component;
      const text = ddb.option('text'); // Keep raw text for standard behavior
      const effectiveSearchText = this.currentSearchText || text; // Prefer currentSearchText
      
      // Enter
      if (keyCode === 13) {
           // Support Add Item on Enter
           // We condition strictly on allowAddNew + presence of text + (Grid Empty OR No Focused Row)
           // avoiding duplicate calls if the logic falls through
           if (this.allowAddNew && effectiveSearchText && (this.isGridEmpty || !this.focusedRowKey)) {
                this.emitAddItem(effectiveSearchText);
                e.event.preventDefault();
                e.event.stopPropagation();
                // Do not close. Wait for data cycle.
                return;
           }

           if (this.isDropDownOpened) {
              // Redundant check removed to simplify
              try { e.event.preventDefault(); } catch {}
              try { e.event.stopPropagation(); } catch {}
              
              // Force select focused even if not strictly "set" by UI yet
              if (this.focusedRowKey) {
                  this.selectCurrentFocusedRow(this.focusedRowKey);
              } else {
                  this.selectCurrentFocusedRow(null);
              }
           }
           return;
      }

      // Arrow Down
      if (keyCode === 40) {
          if (!this.isDropDownOpened) {
              (e.component as any).isKeyDown = true; 
              this.isDropDownOpened = true;
          } else {
              if (this.grid && this.grid.instance) {
                  try { this.grid.instance.focus(); } catch {}
              }
          }
      }
  }

  onGridContentReady(e: any) {
      const dataSource = e.component.getDataSource();
      if (dataSource) {
          const items = dataSource.items();
          this.isGridEmpty = !items || items.length === 0;
          this.cd.detectChanges();
      }

      // Ensure selection matches value (important for dynamic updates like Add New)
      if (this.value && this.grid && this.grid.instance) {
        const keys = this.grid.instance.getSelectedRowKeys();
        if (!keys.includes(this.value)) {
           this.grid.instance.selectRows([this.value], false);
        }
      }

      if (!this.firstLoadCompleted) {
          this.firstLoadCompleted = true;
          if (this.dropDownBox && this.dropDownBox.instance) {
              try { this.dropDownBox.instance.focus(); } catch {}
          }
      }
  }

  onGridKeyDown(e: any) {
      if (e.event.keyCode === 13 && this.focusedRowKey != null) {
          this.focusedRowIndex = e.component.option('focusedRowIndex') || 0;
          this.selectValue(this.focusedRowKey);
          this.isDropDownOpened = false;
      }
  }

  onGridSelectionChanged(e: any) {
      const keys = e.selectedRowKeys;
      if (keys && keys.length > 0) {
        this.selectValue(keys[0]);
        if (this.dropDownBox) {
            // Use timeout to ensure the value prop propagates to the DDB before closing,
            // preventing it from reverting to custom text value or clearing
            setTimeout(() => {
                try { this.dropDownBox.instance.close(); } catch {}
            }, 100);
        }
      }
  }

  private selectCurrentFocusedRow(focusedKey: any) {
      if (focusedKey != null) {
          this.selectValue(focusedKey);
          // Rely on onGridSelectionChanged to close DDB
          return;
      }

      // Fallback: pick first
      this.gridDataSource.load().then((items: any[]) => {
          const firstId = (items && items.length) ? items[0][this.keyExpr] : null;
          if (firstId != null) {
              this.selectValue(firstId);
              // Rely on onGridSelectionChanged to close DDB
          }
      }).catch(() => {});
  }

  private selectValue(val: any) {
      if (this.grid && this.grid.instance) {
          const current = this.grid.instance.getSelectedRowKeys();
           if (!current.includes(val)) {
                this.grid.instance.selectRows([val], false); 
           }
      }

      const prev = this.value;
      this.value = val;
      this.valueChange.emit(val);
      this.onValueChanged.emit({ value: val, previousValue: prev });
      this.updateFormControl(val);
      
      // Find full object to emit
      let item = null;
      // Try to find in loaded items of gridDataSource if simple dataSource array is not enough
      if (this.dataSource && this.dataSource.length > 0) {
          item = this.dataSource.find(x => this.getValueFromExpr(x, this.keyExpr) === val);
      } else if (this.gridDataSource) {
           // items might be loaded in store
           const items = this.gridDataSource.items();
           if (items && items.length) {
               item = items.find(x => this.getValueFromExpr(x, this.keyExpr) === val);
           }
      }

      this.onSelectionChanged.emit({
          value: val,
          selectedItem: item
      });

      // Legacy support for [(selectedItem)]
      if (item && this.selectedItem !== item) {
          this.selectedItem = item;
          this.selectedItemChange.emit(item);
      }

      // Legacy emit
      this.valueSelected.emit(val); 
      this.onElementValueChanged.emit(item);
  }

  emitAddItem(text: string) {
    this.onAddItem.emit(text);
    // Do NOT close here. Let the value update from parent trigger ngOnChanges, 
    // which selects the row, which triggers onGridSelectionChanged, which closes the dropdown.
    // This ensures data is loaded before close (preventing text clear issue).
    
    // Force reset search
    this.currentSearchText = '';
  }
}
